import type { Song, SongListEntry } from 'types';

/**
 * The persistent half of the full-song cache.
 *
 * The catalogue in IndexedDB deliberately holds no playback data (see `songCatalogue.ts`), so
 * `getSongById` reads the full document. Without this cache that read happened again on every
 * reload — open the same song tomorrow, pay for it again — which is a per-song read the old
 * whole-catalogue cache never charged. Keeping the documents we have already paid for turns the
 * second open of a song, and every re-open of a playlist, back into zero reads.
 *
 * The awkward part of caching a full document is knowing when it has gone stale, and the answer is
 * that the catalogue already tells us: every song carries `lastUpdated`, and the catalogue refreshes
 * it on every app load (a delta query, or the 30-day clean fetch). So a cached document is served
 * only while its `lastUpdated` still matches the catalogue's for that song — an edit in the admin
 * panel bumps `lastUpdated`, the next catalogue load sees the new value, and the cached copy stops
 * being used. That makes this cache exactly as stale as the song list itself, and no staler.
 *
 * Everything here is pure and takes its Song types as `import type` only, so it never reaches `fb`
 * (which stands up a real Firebase app at module scope) and can be tested without mocking.
 */

/** Songs kept on disk. Whole catalogues are a few hundred songs at ~9 KB, so this is single-digit MB. */
export const PERSISTED_FULL_SONG_LIMIT = 300;

/**
 * A cached document is dropped after this long even if it still matches the catalogue, so a copy
 * can never outlive the catalogue that vouches for it. Matches `daysBetweenCleanSongFetch`.
 */
export const PERSISTED_FULL_SONG_MAX_AGE_DAYS = 30;

/** What goes into the `fullSongCache` object store, keyed by song id. */
export type PersistedFullSong = {
  /** The full document, minus `favorite` — that is per user and is re-applied from the favourites read. */
  song: Song;
  /** `song.lastUpdated` flattened to milliseconds, so freshness is a number comparison. */
  lastUpdatedMillis: number | undefined;
  /** When we wrote it, in milliseconds. */
  cachedAt: number;
};

/** The access index, kept beside the songs so an eviction never has to read the songs themselves. */
export type FullSongAccessIndex = { [songId: string]: number };

type TimestampLike = { toMillis?: () => number; seconds?: number; nanoseconds?: number };

/**
 * Flattens whatever a `lastUpdated` currently is to milliseconds.
 *
 * It has three shapes in this codebase: a real `firebase.firestore.Timestamp` when it comes
 * straight off a document, a plain `{ seconds, nanoseconds }` after a round trip through IndexedDB
 * (the structured clone drops the prototype, exactly as `JSON.stringify` did before it), and
 * `undefined` on the handful of documents written before the field existed.
 */
export const timestampToMillis = (value: unknown): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  if (typeof value === 'object') {
    const timestamp = value as TimestampLike;
    if (typeof timestamp.toMillis === 'function') {
      return timestamp.toMillis();
    }
    if (typeof timestamp.seconds === 'number') {
      return timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1e6);
    }
  }
  return undefined;
};

/**
 * Whether a cached document may still be served for `catalogueEntry`.
 *
 * Requires a catalogue entry to check against: with no entry there is nothing vouching for the copy,
 * so the caller reads the document instead. `>=` rather than `===` because a document we fetched
 * ourselves can legitimately be *newer* than the catalogue entry that came out of the local cache.
 */
export const isPersistedSongFresh = (
  persisted: PersistedFullSong | undefined,
  catalogueEntry: SongListEntry | undefined,
  nowMillis: number,
  maxAgeMillis: number = PERSISTED_FULL_SONG_MAX_AGE_DAYS * 24 * 3600000,
): boolean => {
  if (!persisted || !persisted.song || !catalogueEntry) {
    return false;
  }
  if (!(persisted.cachedAt > 0) || nowMillis - persisted.cachedAt >= maxAgeMillis) {
    return false;
  }
  const catalogueMillis = timestampToMillis(catalogueEntry.lastUpdated);
  if (catalogueMillis === undefined || persisted.lastUpdatedMillis === undefined) {
    // One side has no version to compare. Re-reading one document is cheaper than being wrong about
    // which bars a song has.
    return false;
  }
  return persisted.lastUpdatedMillis >= catalogueMillis;
};

/** Records that `songId` was just used. Returns the same object for convenience. */
export const touchAccessIndex = (index: FullSongAccessIndex, songId: string, nowMillis: number): FullSongAccessIndex => {
  index[songId] = nowMillis;
  return index;
};

/**
 * The ids to drop to bring the store back under `limit`, least recently used first.
 *
 * Ties break on the id so the result is deterministic — otherwise a playlist written in one tick,
 * where every entry shares a millisecond, would evict in whatever order the keys happened to come
 * out in.
 */
export const selectEntriesToEvict = (index: FullSongAccessIndex, limit: number = PERSISTED_FULL_SONG_LIMIT): string[] => {
  const entries = Object.entries(index);
  if (entries.length <= limit) {
    return [];
  }
  entries.sort((a, b) => (a[1] === b[1] ? (a[0] < b[0] ? -1 : 1) : a[1] - b[1]));
  return entries.slice(0, entries.length - limit).map(([songId]) => songId);
};
